/**
 * Phase 23 (Plan 23-03) — DiagnosticsPage.
 *
 * Super-Admin-only relocation of the Phase-3 health pill + DemoForm that used
 * to live at `/dashboard`. The page is wrapped in a `<RequireRole role="Super Admin">`
 * route guard at the router level — non-Super-Admin navigators redirect to
 * `/dashboard` per D-243.
 *
 * Lifted content:
 *  - StatusPill (was Dashboard.tsx:29-51)
 *  - Backend Health card (was Dashboard.tsx:84-161)
 *  - Demo Form card (was Dashboard.tsx:163-177)
 *  - Clear-session button (was Dashboard.tsx:73-82)
 */

import {
  CheckCircleIcon,
  XCircleIcon,
  SpinnerIcon,
  DatabaseIcon,
  CpuIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api/axios';
import { ApiError, toApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { cn } from '@/lib/utils/cn';
import { DemoForm } from '@/pages/DemoForm';
import type { HealthResponse } from '@/types/api';

async function fetchHealth(): Promise<HealthResponse> {
  try {
    const { data } = await api.get<HealthResponse>('/api/health');
    return data;
  } catch (error) {
    throw toApiError(error);
  }
}

function StatusPill({
  available,
  label,
}: {
  readonly available: boolean;
  readonly label: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-xs font-medium',
        available ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive',
      )}
    >
      {available ? (
        <CheckCircleIcon size={14} weight="fill" />
      ) : (
        <XCircleIcon size={14} weight="fill" />
      )}
      {label}
    </div>
  );
}

export function DiagnosticsPage(): JSX.Element {
  const { user, logout } = useAuth();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<
    HealthResponse,
    ApiError
  >({
    queryKey: ['health'],
    queryFn: fetchHealth,
  });

  return (
    <div className="space-y-6" data-testid="diagnostics-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Diagnostics</h1>
          <p className="text-muted-foreground">
            Super Admin only — system health and demo surfaces. Calls{' '}
            <code className="font-mono text-sm">GET /api/health</code> via the axios client with
            bearer auth attached when a token is present.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            void logout({ skipBackendCall: true });
          }}
          aria-label="Clear session"
        >
          Clear session
        </Button>
      </div>

      <Card data-testid="diagnostics-health-card">
        <CardHeader>
          <CardTitle>Backend Health</CardTitle>
          <CardDescription>
            Live response from the Phase 1 health endpoint.
            {user && (
              <span className="block mt-1">
                Logged in as <strong className="text-foreground">{user.fullName}</strong> (
                {user.email}).
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <SpinnerIcon size={16} className="animate-spin" />
              Loading…
            </div>
          )}
          {isError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <strong>Error {error.status}:</strong> {error.message}
              {error.traceId !== undefined && (
                <div className="mt-1 text-xs font-mono opacity-80">trace: {error.traceId}</div>
              )}
            </div>
          )}
          {data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Service</div>
                  <div className="font-medium">{data.service}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Version</div>
                  <div className="font-medium">{data.version}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Status</div>
                  <div className="font-medium capitalize">{data.status}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Timestamp</div>
                  <div className="font-mono text-xs">{data.timestamp}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusPill
                  available={data.checks.database === 'available'}
                  label={`Database: ${data.checks.database}`}
                />
                <StatusPill
                  available={data.checks.redis === 'available'}
                  label={`Redis: ${data.checks.redis}`}
                />
              </div>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void refetch();
            }}
            disabled={isFetching}
            aria-label="Refetch /api/health"
          >
            {isFetching ? (
              <SpinnerIcon size={14} className="mr-2 animate-spin" />
            ) : (
              <DatabaseIcon size={14} className="mr-2" />
            )}
            Refetch
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="diagnostics-demo-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CpuIcon size={18} weight="duotone" />
            Demo form (proves SC5)
          </CardTitle>
          <CardDescription>
            React Hook Form + Zod resolver. Validates name (3-50 chars) and fires a Sonner toast on
            success or validation error.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DemoForm />
        </CardContent>
      </Card>
    </div>
  );
}
